import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { preview12Month, previewTCV, money, FORM_GRID_3 } from "@/components/opportunities/shared";
import { buildCloseWonForm, buildCloseWonPayload, WON_COPY } from "@/components/opportunities/opportunityForm";
import { CircleNotch, Trophy, XCircle, ArrowCounterClockwise } from "@phosphor-icons/react";

function Fld({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">{label}{required ? " *" : ""}</Label>
      {children}
    </div>
  );
}

async function submitAction(fn, setLoading, onDone, onClose) {
  setLoading(true);
  try {
    await fn();
    onDone();
    onClose();
  } catch (err) {
    toast.error(formatApiError(err.response?.data?.detail));
  } finally {
    setLoading(false);
  }
}

// Closed-Won: a salesperson PREPARES (request), an administrator CONFIRMS.
export function CloseWonDialog({ open, opportunity, isAdmin, onClose, onDone }) {
  const o = opportunity;
  const copy = WON_COPY[isAdmin ? "admin" : "salesperson"];
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(() => buildCloseWonForm(o));
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const twelveMonth = preview12Month(form.monthly_recurring_revenue, form.one_time_revenue, form.contract_term_months);
  const tcv = previewTCV(form.monthly_recurring_revenue, form.one_time_revenue, form.contract_term_months);

  const submit = (e) => {
    e.preventDefault();
    submitAction(async () => {
      await api.post(`/opportunities/${o.id}/${copy.action}`, buildCloseWonPayload(form, o.version));
      toast.success(copy.success);
    }, setLoading, onDone, onClose);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="close-won-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl font-bold text-[#032852]">
            <Trophy size={20} className="text-[#569A1F]" /> {copy.title}
          </DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>
        {o.close_requested && isAdmin && (
          <p className="rounded-sm bg-[#F59E0B]/10 px-3 py-2 text-xs font-semibold text-[#B45309]" data-testid="close-requested-banner">
            This opportunity was prepared for closing by the salesperson and awaits your confirmation.
          </p>
        )}
        <form onSubmit={submit} className="space-y-4">
          <Fld label="Service start date" required>
            <DatePicker value={form.service_start_date} onChange={set("service_start_date")} required testId="won-service-start" />
          </Fld>
          {!o.origin_classification && (
            <>
              <Fld label="Quota Attribution (Lead Origin)" required>
                <Select value={form.origin_classification} onValueChange={set("origin_classification")}>
                  <SelectTrigger className="rounded-sm" data-testid="won-origin"><SelectValue placeholder="Select classification — required to close won" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="shirley_originated">Self-Originated (Owner-Generated) — 100% quota credit</SelectItem>
                    <SelectItem value="company_assigned">Company/Owner-Assigned — 50% quota credit</SelectItem>
                    <SelectItem value="tag_work">Tag Work — tracked separately</SelectItem>
                  </SelectContent>
                </Select>
              </Fld>
              {form.origin_classification === "shirley_originated" && (
                <Fld label="Origination justification" required>
                  <Textarea required value={form.origin_justification} onChange={set("origin_justification")} rows={2}
                    placeholder="Document prospect development, decision-maker access, follow-up and proposal contribution"
                    className="rounded-sm" data-testid="won-origin-justification" />
                </Fld>
              )}
            </>
          )}
          <div className={FORM_GRID_3}>
            <Fld label="Monthly recurring $"><Input type="number" min="0" step="0.01" value={form.monthly_recurring_revenue} onChange={set("monthly_recurring_revenue")} className="rounded-sm" data-testid="won-mrr" /></Fld>
            <Fld label="One-time $"><Input type="number" min="0" step="0.01" value={form.one_time_revenue} onChange={set("one_time_revenue")} className="rounded-sm" data-testid="won-one-time" /></Fld>
            <Fld label="Term (months)"><Input type="number" min="1" value={form.contract_term_months} onChange={set("contract_term_months")} className="rounded-sm" data-testid="won-term" /></Fld>
          </div>
          <div className="space-y-1 rounded-sm bg-[#569A1F]/10 px-3 py-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wide text-[#457d18]">First 12-Month Contract Value</span>
              <span className="font-heading text-lg font-black text-[#457d18]" data-testid="won-12mo-preview">{money(twelveMonth)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wide text-[#032852]">
                Total Contract Value{form.contract_term_months ? ` (${form.contract_term_months}-month term)` : ""}
              </span>
              <span className="font-heading text-base font-black text-[#032852]" data-testid="won-tcv-preview">{tcv !== null ? money(tcv) : "—"}</span>
            </div>
            <p className="text-[10px] leading-4 text-[#52525B]">
              The first 12-month value is credited once toward quota in the configured quota period — it is not the total
              contract value. TCV covers the full signed term and is informational only: it never creates quota credit or
              commission. If contracted pricing changes during the term, record the year-by-year amounts in closing notes —
              TCV must not assume today&apos;s price for future years.
            </p>
          </div>
          <Fld label="Closing notes"><Textarea value={form.closing_notes} onChange={set("closing_notes")} rows={2} className="rounded-sm" data-testid="won-notes" /></Fld>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]" data-testid="submit-close-won">
              {loading ? <CircleNotch size={18} className="animate-spin" /> : copy.submit}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function CloseLostDialog({ open, opportunity, onClose, onDone }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ loss_reason: "", competitor: "", closing_notes: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    submitAction(async () => {
      await api.post(`/opportunities/${opportunity.id}/close-lost`, {
        loss_reason: form.loss_reason, competitor: form.competitor || null,
        closing_notes: form.closing_notes || null, version: opportunity.version,
      });
      toast.success("Opportunity closed as lost");
    }, setLoading, onDone, onClose);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="close-lost-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl font-bold text-[#032852]">
            <XCircle size={20} className="text-[#EF4444]" /> Close as Lost
          </DialogTitle>
          <DialogDescription>A loss reason is required. The record is preserved for review.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Fld label="Loss reason" required>
            <Input required value={form.loss_reason} onChange={set("loss_reason")} className="rounded-sm" placeholder='e.g. "Stayed with incumbent"' data-testid="lost-reason" />
          </Fld>
          <Fld label="Competitor"><Input value={form.competitor} onChange={set("competitor")} className="rounded-sm" data-testid="lost-competitor" /></Fld>
          <Fld label="Closing notes"><Textarea value={form.closing_notes} onChange={set("closing_notes")} rows={2} className="rounded-sm" data-testid="lost-notes" /></Fld>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} className="rounded-sm bg-[#EF4444] font-semibold text-white hover:bg-[#DC2626]" data-testid="submit-close-lost">
              {loading ? <CircleNotch size={18} className="animate-spin" /> : "Close as Lost"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Administrator-only: decline a pending Closed-Won request with a mandatory
// reason. All opportunity and organization data are preserved.
export function RejectCloseDialog({ open, opportunity, onClose, onDone }) {
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState("");

  const submit = (e) => {
    e.preventDefault();
    submitAction(async () => {
      await api.post(`/opportunities/${opportunity.id}/reject-close-won`, {
        reason, version: opportunity.version,
      });
      toast.success("Closed-Won request rejected — the requester has been notified");
    }, setLoading, onDone, onClose);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="reject-close-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl font-bold text-[#032852]">
            <XCircle size={20} className="text-[#EF4444]" /> Reject Closed-Won Request
          </DialogTitle>
          <DialogDescription>
            The opportunity stays open and nothing is deleted. Your decision and reason are
            recorded and the requesting salesperson is notified.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Fld label="Rejection reason" required>
            <Textarea required value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
              placeholder='e.g. "Pricing schedule incomplete — confirm the signed term first"'
              className="rounded-sm" data-testid="reject-close-reason" />
          </Fld>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} className="rounded-sm bg-[#EF4444] font-semibold text-white hover:bg-[#DC2626]" data-testid="submit-reject-close">
              {loading ? <CircleNotch size={18} className="animate-spin" /> : "Reject Request"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Administrator-only: reopen a closed opportunity with a mandatory reason.
export function ReopenDialog({ open, opportunity, stages, onClose, onDone }) {
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState("");
  const [stageId, setStageId] = useState("");
  const openStages = (stages || []).filter((s) => !s.name.startsWith("Closed"));

  const submit = (e) => {
    e.preventDefault();
    submitAction(async () => {
      await api.post(`/opportunities/${opportunity.id}/reopen`, { reason, stage_id: stageId || null });
      toast.success("Opportunity reopened");
    }, setLoading, onDone, onClose);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="reopen-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl font-bold text-[#032852]">
            <ArrowCounterClockwise size={20} className="text-[#032852]" /> Reopen Opportunity
          </DialogTitle>
          <DialogDescription>Administrator action — recorded in the stage history with your reason.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Fld label="Reason" required>
            <Input required value={reason} onChange={(e) => setReason(e.target.value)} className="rounded-sm" data-testid="reopen-reason" />
          </Fld>
          <Fld label="Return to stage">
            <Select value={stageId} onValueChange={setStageId}>
              <SelectTrigger className="rounded-sm" data-testid="reopen-stage"><SelectValue placeholder="Negotiation (default)" /></SelectTrigger>
              <SelectContent>{openStages.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </Fld>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} className="rounded-sm bg-[#032852] font-semibold text-white hover:bg-[#032852]/90" data-testid="submit-reopen">
              {loading ? <CircleNotch size={18} className="animate-spin" /> : "Reopen"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
